<script setup>
import { DateInput } from '@addictivegroup/addictive-datetime'
import { computed } from 'vue'
import { todayIso, formatDate } from '@/lib/format.js'
import NumberInput from './NumberInput.vue'

/**
 * An offer's validity, typed either way (D-195): as days — what an offer mostly says, counted
 * from the day it is sent — or as a date it binds until. Each fills the other: days typed show
 * the date it would be if sent today; a date picked shows its days.
 */
const props = defineProps({ inputs: { type: Object, required: true }, disabled: Boolean, id: { type: String, default: '' } })
const emit = defineEmits(['set'])
const DAY = 86400000
const days = (from, to) => Math.round((Date.UTC(...to.split('-').map((v, i) => Number(v) - (i === 1 ? 1 : 0))) - Date.UTC(...from.split('-').map((v, i) => Number(v) - (i === 1 ? 1 : 0)))) / DAY)
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}
const fixed = computed(() => props.inputs.valid_until || null)
const shownDays = computed(() => (fixed.value ? Math.max(0, days(todayIso(), fixed.value)) : props.inputs.terms?.validity_days ?? null))
const shownDate = computed(() => fixed.value || (shownDays.value ? addDays(todayIso(), Number(shownDays.value)) : ''))

function setDays(n) {
  const value = n ? Math.max(1, Math.round(Number(n))) : null
  // Days count from sending: no fixed date.
  emit('set', 'valid_until', null)
  emit('set', 'terms', { ...(props.inputs.terms || {}), validity_days: value })
}
function setDate(v) {
  emit('set', 'valid_until', v || null)
  if (v) emit('set', 'terms', { ...(props.inputs.terms || {}), validity_days: Math.max(1, days(todayIso(), v)) })
}
</script>

<template>
  <div>
    <div class="flex flex-wrap items-center gap-2">
      <NumberInput :id="id || undefined" :model-value="shownDays" integer :min="1" :max="365" class="w-20" :disabled="disabled" data-testid="validity-days" @change="setDays" />
      <span class="text-sm text-muted">{{ $t('offers.validity.days') }}</span>
      <span class="text-sm text-muted">{{ $t('offers.validity.until') }}</span>
      <DateInput :class="['w-48', fixed ? '' : '[&_input]:text-muted']" direction="future" :min="todayIso()" :model-value="shownDate || null" :clearable="false" :disabled="disabled" data-testid="validity-date" @update:model-value="(v) => setDate(v || '')" />
    </div>
    <p class="mt-1 text-xs text-faint" data-testid="validity-hint">{{ fixed ? $t('offers.validity.fixed', { date: formatDate(fixed) }) : $t('offers.validity.fromSending') }}</p>
  </div>
</template>
